import React, { forwardRef } from 'react';

const InputField = forwardRef(({
    name,
    label,
    value,
    handleChange,
    onFocus,
    onBlur, // Aggiunta la prop onBlur
    options = [],
    type = 'text',
    checked = false,
    icon = null,
    helperText = '',
    placeholder = '',
    labelIcon = null,
    labelIconOnClick = () => {},
    min,
    // Nuove props per gestione errore:
    error = false,
    errorText = '',
    // Nuova prop per disabilitare il campo
    disabled = false,
}, ref) => {
    const handleWheel = (e) => {
        if (type === 'number') {
            e.target.blur();
        }
    };

    const handleKeyDown = (e) => {
        if (type === 'number' && (e.key === '-' || e.key === 'e')) {
            e.preventDefault();
        }
    };

    const inputStyles = {
        MozAppearance: 'textfield',
        appearance: 'none',
        '&::-webkit-outer-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
        '&::-webkit-inner-spin-button': {
            WebkitAppearance: 'none',
            margin: 0,
        },
    };

    // ------ RENDER PER SELECT ------
    if (type === 'select') {
        return (
            <div className="relative z-0 w-full mb-6 group">
                <select
                    ref={ref}
                    name={name}
                    id={name}
                    value={value}
                    onChange={handleChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    className={`block py-4 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 appearance-none focus:outline-none focus:ring-0 peer ${
                        disabled ? 'opacity-50 cursor-not-allowed' : ''
                    } ${
                        error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-wild-ocean'
                    }`}
                >
                    <option value="">Seleziona...</option>
                    {options.map((option, index) => (
                        <option key={index} value={option.value || option}>
                            {option.label || option}
                        </option>
                    ))}
                </select>

                <label
                    htmlFor={name}
                    className="absolute text-md font-heading uppercase font-semibold text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:text-wild-ocean peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                >
                    <span className="flex items-center">
                        {label}
                        {labelIcon && (
                            <button
                                type="button"
                                className="ml-2 inline-flex items-center text-gray-500 hover:text-gray-700"
                                onClick={labelIconOnClick}
                                disabled={disabled}
                            >
                                <img src={labelIcon} alt="label icon" className="w-6 h-6" />
                            </button>
                        )}
                    </span>
                </label>

                {(error && errorText) ? (
                    <p className="text-xs text-red-500 mt-1">{errorText}</p>
                ) : (
                    helperText && <p className="text-xs text-gray-500 mt-1">{helperText}</p>
                )}
            </div>
        );
    }

    // ------ RENDER PER CHECKBOX ------
    if (type === 'checkbox') {
        return (
            <div className="mb-7 group flex items-center">
                <input
                    ref={ref}
                    type="checkbox"
                    name={name}
                    id={name}
                    checked={checked}
                    onChange={handleChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    autoComplete="off"
                    style={{ accentColor: '#10B981' }}
                    className={`w-6 h-6 bg-gray-100 border-wild-ocean rounded-xl focus:ring-2 ${
                        disabled ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                />
                <label
                    htmlFor={name}
                    className="ml-2 text-sm text-gray-900 flex items-center"
                >
                    {label}
                    {labelIcon && (
                        <button
                            type="button"
                            className="ml-2 inline-flex items-center text-gray-500 hover:text-gray-700"
                            onClick={labelIconOnClick}
                            disabled={disabled}
                        >
                            <img src={labelIcon} alt="label icon" className="w-6 h-6" />
                        </button>
                    )}
                </label>
                {helperText && <p className="text-xs text-gray-500 ml-6">{helperText}</p>}
            </div>
        );
    }

    // ------ RENDER PER TEXTAREA ------
    if (type === 'textarea') {
        return (
            <div className="relative z-0 w-full mb-6 group">
                <textarea
                    ref={ref}
                    name={name}
                    id={name}
                    value={value}
                    onChange={handleChange}
                    onBlur={onBlur}
                    disabled={disabled}
                    rows="8"
                    className={`block py-3 px-3 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 rounded-md focus:outline-none focus:ring-0 resize-none peer ${
                        disabled ? 'opacity-50 cursor-not-allowed' : ''
                    } ${
                        error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-wild-ocean'
                    }`}
                    placeholder=" "
                    onFocus={onFocus}
                ></textarea>

                <label
                    htmlFor={name}
                    className="absolute text-sm text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:text-wild-ocean peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
                >
                    <span className="flex items-center">
                        {label}
                        {labelIcon && (
                            <button
                                type="button"
                                className="ml-2 inline-flex items-center text-gray-500 hover:text-gray-700"
                                onClick={labelIconOnClick}
                                disabled={disabled}
                            >
                                <img src={labelIcon} alt="label icon" className="w-6 h-6" />
                            </button>
                        )}
                    </span>
                </label>

                {(error && errorText) ? (
                    <p className="text-xs text-red-500 mt-1">{errorText}</p>
                ) : (
                    helperText && <p className="text-xs text-gray-500 mt-1">{helperText}</p>
                )}
            </div>
        );
    }

    // ------ RENDER PER ALTRI TIPI (es. text, number, datetime-local, ecc.) ------
    return (
        <div className="relative z-0 w-full mb-6 group">
            <input
                ref={ref}
                type={type}
                name={name}
                id={name}
                value={value}
                onChange={handleChange}
                onBlur={onBlur}  // Aggiunta onBlur
                onWheel={handleWheel}
                onKeyDown={handleKeyDown}
                min={typeof min !== 'undefined' ? min : (type === 'number' ? '0' : undefined)}
                disabled={disabled}
                className={`block py-4 px-0 w-full text-sm text-gray-900 bg-transparent border-0 border-b-2 appearance-none focus:outline-none focus:ring-0 peer ${
                    disabled ? 'opacity-50 cursor-not-allowed' : ''
                } ${
                    error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-wild-ocean'
                }`}
                placeholder=" "
                style={type === 'number' ? inputStyles : {}}
                onFocus={onFocus}
                autoComplete="off"
            />

            {icon && (
                <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <img src={icon} alt="Card Icon" className="w-6 h-6" />
                </div>
            )}

            <span
                className={`absolute text-sm text-gray-400 left-0 top-2.5 transition-opacity duration-300 
                            ${!value ? 'peer-focus:opacity-100 opacity-0' : 'opacity-0'}`}
            >
                {placeholder}
            </span>

            <label
                htmlFor={name}
                className="absolute text-md font-heading uppercase font-semibold text-gray-500 duration-300 transform -translate-y-6 scale-75 top-3 origin-[0] peer-focus:left-0 peer-focus:text-wild-ocean peer-placeholder-shown:scale-100 peer-placeholder-shown:translate-y-0 peer-focus:scale-75 peer-focus:-translate-y-6"
            >
                <span className="flex items-center">
                    {label}
                    {labelIcon && (
                        <button
                            type="button"
                            className="ml-2 inline-flex items-center text-gray-500 hover:text-gray-700"
                            onClick={labelIconOnClick}
                            disabled={disabled}
                        >
                            <img src={labelIcon} alt="label icon" className="w-6 h-6 mb-1" />
                        </button>
                    )}
                </span>
            </label>

            {(error && errorText) ? (
                <p className="text-xs text-red-500 mt-1">{errorText}</p>
            ) : (
                helperText && <p className="text-xs text-gray-500 mt-1">{helperText}</p>
            )}
        </div>
    );
});

export default InputField;
